import { AspectRatio, Text, Flex, type FlexProps, type AspectRatioProps } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import React, { type ReactNode } from "react";
import { remCalc } from "theme/utils";
import { ButtonGroup } from "ui/button";
import { Chip, type ChipProps } from "ui/chips";

export type CardProps = {
  /**
   * The color theme of the card used for text colors.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * The style variant of the card.
   *
   * @default "inline"
   */
  variant: "inline" | "shadow" | "fill";

  /**
   * The alignment of the card content.
   *
   * @default "left"
   */
  align?: "left" | "center";

  /**
   * The media to render in the card.
   *
   * Can be either an image or lottie animation
   */
  media: ReactNode;

  /**
   * The aspect ratio of the media.
   *
   * @default 1/1
   */
  mediaRatio?: AspectRatioProps["ratio"];

  /**
   * Optional chip that serves as lead-in text for the card.
   */
  chip?: {
    /**
     * The chip variant
     *
     * @default "transparent"
     */
    variant?: ChipProps["variant"];
    /**
     * The chip text.
     */
    text: string;
  };

  /**
   * The title of the card.
   */
  title: ReactNode;

  /**
   * Optional description of the card.
   */
  description?: string;

  /**
   * Optional CTA buttons
   *
   * If using two, place the secondary button on the left, and the primary on the right.
   */
  actions?: ReactNode;

  /**
   * Optional max width for the card.
   *
   * @default "{base: '100%', sm: remCalc(435)}"
   */
  maxW?: FlexProps["maxW"];
};
export function Card({
  chip,
  title,
  description,
  actions,
  media,
  mediaRatio = 1 / 1,
  variant = "inline",
  theme = "light",
  align = "left",
  maxW = { base: "100%", sm: remCalc(435) },
}: CardProps) {
  const alignItems = align === "left" ? "start" : "center";
  const layerStyle = variant === "inline" ? "card.inline" : `card.${variant}.${theme}`;

  return (
    <Flex layerStyle={layerStyle} align={alignItems} textAlign={align} maxW={maxW}>
      <AspectRatio layerStyle="card.media" bg={`brand.backgroundSecondary.${theme}`} ratio={mediaRatio}>
        {media}
      </AspectRatio>

      <Flex align={alignItems} direction="column" gap="small" px={variant === "inline" ? 0 : "smaller"}>
        {chip && <Chip variant={chip.variant}>{chip.text}</Chip>}

        <Text align={alignItems} color={`brand.text.${theme}`} textStyle="headline3">
          {title}
        </Text>

        <Text color={variant === "fill" ? "new.gray.900" : `brand.textSecondary.${theme}`} textStyle="caption1">
          {description}
        </Text>

        {actions && <ButtonGroup>{actions}</ButtonGroup>}
      </Flex>
    </Flex>
  );
}
